import { Suspense, useEffect, useRef, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { landingFor, satisfies, type Requirement } from "@/domain/rbac";
import { api } from "@/data";
import { useMe } from "@/data/queries";
import { LoadingRows } from "@/components/ui/States";
import ForbiddenPage from "@/features/system/ForbiddenPage";

export function FullScreenLoading() {
  return (
    <div className="flex min-h-dvh items-center justify-center" role="status" aria-label="Loading">
      <div className="size-8 animate-spin rounded-full border-2 border-border-strong border-t-accent" />
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { data: me, isLoading } = useMe();
  const location = useLocation();
  if (isLoading) return <FullScreenLoading />;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <>{children}</>;
}

export function RequirePermission({ require, devOnly, children }: { require: Requirement; devOnly?: boolean; children: ReactNode }) {
  const { data: me } = useMe();
  const location = useLocation();
  const allowed = devOnly ? !!me?.isDev : satisfies(me, require);
  const logged = useRef<string | null>(null);
  useEffect(() => {
    if (!allowed && me && logged.current !== location.pathname) {
      logged.current = location.pathname;
      api.audit.recordDenied(location.pathname).catch(() => undefined);
    }
  }, [allowed, me, location.pathname]);
  if (!allowed) return <ForbiddenPage require={devOnly ? null : require} />;
  return <Suspense fallback={<LoadingRows rows={5} />}>{children}</Suspense>;
}

export function RoleLanding() {
  const { data: me, isLoading } = useMe();
  if (isLoading) return <FullScreenLoading />;
  return <Navigate to={landingFor(me)} replace />;
}

/** The login page sends signed-in users to their workspace. */
export function PublicOnly({ children }: { children: ReactNode }) {
  const { data: me, isLoading } = useMe();
  if (isLoading) return <FullScreenLoading />;
  if (me) return <Navigate to={landingFor(me)} replace />;
  return <>{children}</>;
}
